import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useRevealProps } from '../components/Reveal'
import { compare, IDS } from '../content'
import LineIcon from '../icons'
import { fillOffer } from '../offer'
import {
  cyan,
  cyanInk,
  Eyebrow,
  greyBg,
  greyMark,
  H2,
  Hi,
  Hl,
  ice,
  ink,
  Lede,
  line,
  muted,
  paper,
  PHONE,
  pulse,
  sans,
  ScreenReader,
  Section,
  SectionHead,
  Small,
  soft,
  Stack,
  Take,
  teal,
  text,
  ui,
  Wrap,
} from '../styled'

const pie = keyframes`
  from { stroke-dasharray: 0 100; }
  to { stroke-dasharray: 25 100; }
`

const Headline = styled(H2)`
  font-size: clamp(2.3rem, 5vw, 4.1rem);
  line-height: 1.02;
  letter-spacing: -0.05em;
`

const Three = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 20px;
`

const Card = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 28px;
  border-radius: 24px;
  background: ${paper};
  border: 1px solid ${line};

  figcaption {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  figcaption b {
    font: 700 19px/1.3 ${sans};
    letter-spacing: -0.015em;
    color: ${ink};
  }

  figcaption span {
    color: ${muted};
    font-size: 15px;
    line-height: 1.55;
  }
`

const Figure = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;

  svg {
    width: 128px;
    height: 128px;
    flex: none;
  }
`

const Key = styled.p`
  margin: 0;
  font: 800 clamp(2.4rem, 3.8vw, 3.1rem) / 1 ${sans};
  letter-spacing: -0.04em;
  color: ${ink};
  white-space: nowrap;

  span {
    font-size: 0.42em;
    letter-spacing: 0;
  }
`

const Map = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
`

const Axis = styled.span`
  position: absolute;
  font: 700 11px/1 ${ui};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${soft};
  white-space: nowrap;
`

const Point = styled.span`
  position: absolute;
  left: ${(props) => props.$left}%;
  top: ${(props) => props.$top}%;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: translate(-7px, -50%);
  font: 600 13px/1.2 ${sans};
  color: ${ink};
  white-space: nowrap;

  i {
    position: relative;
    width: 14px;
    height: 14px;
    flex: none;
    border-radius: 50%;
    background: ${greyMark};
  }

  ${(props) =>
    props.$flip &&
    css`
      flex-direction: row-reverse;
      transform: translate(calc(-100% + 7px), -50%);
    `}

  ${(props) =>
    props.$me &&
    css`
      font-size: 16px;
      font-weight: 800;
      transform: translate(-9px, -50%);

      i {
        width: 18px;
        height: 18px;
        background: ${cyan};
        box-shadow: 0 0 0 4px ${paper};
      }

      i::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: ${cyan};
        opacity: 0;
        transform-origin: center;
        animation: ${pulse} 2.4s ease-out infinite;
      }
    `}
`

// the quarter hour fills once the card is on screen
const Clock = styled.svg`
  .pie {
    stroke-dasharray: 0 100;
  }

  [data-in] & .pie,
  :not([data-js]) & .pie {
    animation: ${pie} 1.2s ease 0.2s both;
  }
`

const Waffle = styled.svg`
  .pulse {
    opacity: 0;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${pulse} 2.6s ease-out infinite;
  }
`

const SwipeHint = styled.p`
  display: none;
  margin: 0;
  font: 700 12px/1 ${ui};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${soft};

  @media ${PHONE} {
    display: block;
  }
`

const TableBox = styled.div`
  overflow-x: auto;
  border-radius: 22px;
  border: 1px solid ${line};
  background: ${paper};
`

const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 15px;
  line-height: 1.4;
  color: ${text};

  th,
  td {
    padding: 16px 18px;
    text-align: left;
    vertical-align: middle;
    border-bottom: 1px solid ${line};
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: 0;
  }

  thead th {
    font: 700 12px/1.2 ${ui};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${soft};
  }

  tbody th {
    font-weight: 600;
    width: 24%;
    color: ${ink};
  }

  .us {
    background: ${ice};
    font-weight: 700;
    color: ${ink};
  }

  thead .us {
    color: ${teal};
  }

  small {
    display: block;
    margin-top: 3px;
    font-size: 13px;
    font-weight: 500;
    color: ${muted};
  }
`

const Mark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  i {
    width: 24px;
    height: 24px;
    flex: none;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: ${(props) => (props.$yes ? cyan : greyBg)};
    color: ${(props) => (props.$yes ? cyanInk : soft)};
    border: ${(props) => (props.$yes ? 0 : `1px solid ${line}`)};
  }

  svg {
    width: 13px;
    height: 13px;
    stroke-width: 2.8;
  }
`

// a cell of several marks, one under the other
const Lines = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Cell = ({ cell, offer }) => {
  if (typeof cell === 'string') return fillOffer(cell, offer) || ''
  if (Array.isArray(cell)) {
    return (
      <Lines>
        {cell.map((line) => (
          <Cell key={line.yes || line.no} cell={line} offer={offer} />
        ))}
      </Lines>
    )
  }
  const yes = cell.yes !== undefined
  return (
    <Mark $yes={yes}>
      <i>
        <LineIcon name={yes ? 'check' : 'cross'} />
      </i>
      {cell.note ? (
        <span>
          {cell.no}
          <small>{cell.note}</small>
        </span>
      ) : (
        cell.yes || cell.no
      )}
    </Mark>
  )
}

const { map, clock, waffle } = compare

/** The positioning map: price against personal attention, with Jumpstart in the corner most options miss. */
export const PositioningMap = () => (
  <Card {...useRevealProps()}>
    <Map role="img" aria-label={map.label}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <rect x="9" y="4" width="43" height="43" rx="2" style={{ fill: ice }} />
        <path
          d="M53 4 V 92 M9 48 H 98"
          style={{
            fill: 'none',
            stroke: line,
            strokeWidth: 1,
            strokeDasharray: '3 4',
            vectorEffect: 'non-scaling-stroke',
          }}
        />
        <path
          d="M9 4 V 92 H 98"
          style={{ fill: 'none', stroke: greyMark, strokeWidth: 1.5, vectorEffect: 'non-scaling-stroke' }}
        />
      </svg>
      <Axis style={{ left: '9%', bottom: 0 }}>{map.axes.lower}</Axis>
      <Axis style={{ right: '2%', bottom: 0 }}>{map.axes.higher}</Axis>
      <Axis style={{ left: 0, top: '4%', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
        {map.axes.personal}
      </Axis>
      {map.points.map((p) => (
        <Point key={p.label} $left={p.left} $top={p.top} $me={p.me} $flip={p.flip}>
          <i />
          {p.label}
        </Point>
      ))}
    </Map>
    <figcaption>
      <b>{map.caption}</b>
      <span>{map.note}</span>
    </figcaption>
  </Card>
)

/** The comparison table, with its swipe hint on a phone; `table` lets another page vary the cells. */
export const CompareTable = ({ offer, table = compare.table }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <SwipeHint aria-hidden="true">{compare.swipeHint}</SwipeHint>
    <TableBox {...useRevealProps()}>
      <Table>
        <caption>
          <ScreenReader>{table.caption}</ScreenReader>
        </caption>
        <thead>
          <tr>
            <th scope="col">
              <ScreenReader>{table.comparedOn}</ScreenReader>
            </th>
            {table.columns.map((column, index) => (
              <th key={column} scope="col" className={index === 0 ? 'us' : undefined}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {row.cells.map((cell, index) => (
                <td key={table.columns[index]} className={index === 0 ? 'us' : undefined}>
                  <Cell cell={cell} offer={offer} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableBox>
  </div>
)

/** "What's different?": the three visual comparisons and the table. */
const CompareSection = ({ offer }) => (
  <Section id={IDS.compare} style={{ padding: 'clamp(44px, 5.5vw, 72px) 24px' }}>
    <Wrap>
      <Stack>
        <SectionHead $maxWidth={880} style={{ gap: 22 }}>
          <Eyebrow {...useRevealProps()}>{compare.eyebrow}</Eyebrow>
          <Headline {...useRevealProps()}>
            {compare.headline} <Hi>{compare.headlineAccent}</Hi>
          </Headline>
          <Lede {...useRevealProps()}>
            {compare.lede}
            <b style={{ color: ink }}>{compare.ledeStrong}</b>
          </Lede>
        </SectionHead>
        <Three>
          <PositioningMap />
          <Card {...useRevealProps()}>
            <Figure>
              <Clock viewBox="0 0 120 120" role="img" aria-label={clock.label}>
                <circle cx="60" cy="60" r="56" style={{ fill: paper, stroke: line, strokeWidth: 2 }} />
                <circle
                  className="pie"
                  cx="60"
                  cy="60"
                  r="25"
                  pathLength="100"
                  transform="rotate(-90 60 60)"
                  style={{ fill: 'none', stroke: cyan, strokeWidth: 50 }}
                />
                <circle
                  cx="60"
                  cy="60"
                  r="51"
                  pathLength="120"
                  style={{
                    fill: 'none',
                    stroke: greyMark,
                    strokeWidth: 5,
                    strokeDasharray: '1.2 8.8',
                    strokeDashoffset: 0.6,
                  }}
                />
                <path
                  d="M60 60 V 16 M60 60 H 100"
                  style={{ fill: 'none', stroke: ink, strokeWidth: 3, strokeLinecap: 'round' }}
                />
                <circle cx="60" cy="60" r="4.5" style={{ fill: ink }} />
              </Clock>
              <Key>
                {clock.figure}
                <span>{clock.unit}</span>
              </Key>
            </Figure>
            <figcaption>
              <b>{clock.caption}</b>
              <span>
                {clock.note[0]}
                {offer.price || '…'}
                {clock.note[1]}
              </span>
            </figcaption>
          </Card>
          <Card {...useRevealProps()}>
            <Figure>
              <Waffle viewBox="0 0 100 100" role="img" aria-label={waffle.label}>
                <defs>
                  <pattern id="waffle-dots" width="10" height="10" patternUnits="userSpaceOnUse">
                    <circle cx="5" cy="5" r="3.3" style={{ fill: line }} />
                  </pattern>
                </defs>
                <rect x="0" y="0" width="100" height="100" fill="url(#waffle-dots)" />
                {[5, 15, 25].map((cx, index) => (
                  <circle
                    key={cx}
                    className="pulse"
                    cx={cx}
                    cy="5"
                    r="3.3"
                    style={{ fill: cyan, animationDelay: `${index * 0.3}s` }}
                  />
                ))}
                {[5, 15, 25].map((cx) => (
                  <circle key={cx} cx={cx} cy="5" r="3.3" style={{ fill: cyan }} />
                ))}
              </Waffle>
              <Key>
                {waffle.figure}
                <span>{waffle.unit}</span>
              </Key>
            </Figure>
            <figcaption>
              <b>{waffle.caption}</b>
              <span>{waffle.note}</span>
            </figcaption>
          </Card>
        </Three>
        <CompareTable offer={offer} />
        <Take $maxWidth={42} {...useRevealProps()}>
          {compare.take[0]}
          <Hl>{compare.take[1]}</Hl>
        </Take>
        <Small $maxWidth={84}>{compare.note}</Small>
      </Stack>
    </Wrap>
  </Section>
)

export default CompareSection
